/**
 * This module handles the document upload process using redux-saga.
 * It manages the upload lifecycle including progress updates, success, and error handling.
 */

import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, take } from "redux-saga/effects";
import { selectUserId } from "../../auth/store/authSelectors";
import { trackEvent } from "../../analytics/store/analyticsSlice";
import {
  uploadError,
  uploadProgress,
  uploadSuccess,
} from "./documentUploadsSlice";
import { createUploadChannel } from "./uploadChannel";

/**
 * Saga that manages a single document upload process.
 * It creates an upload channel and handles various upload events (progress, success, error).
 *
 * @param action - Redux action containing upload details (id and file)
 * @yields Various effects for managing the upload process
 */
export function* handleUploadDocument(
  action: PayloadAction<{ id: string; file: File }>
): Generator<any, void, any> {
  let channel;
  try {
    if (action.payload.file.size === 0) {
      yield put(
        uploadError({
          id: action.payload.id,
          error: "File is empty. Please select a valid file to upload.",
        })
      );
      return;
    }

    const uid = yield select(selectUserId);
    channel = yield call(createUploadChannel, uid, action.payload.file);

    try {
      while (true) {
        const event = yield take(channel);
        switch (event.type) {
          case "progress":
            yield put(
              uploadProgress({
                id: action.payload.id,
                progress: event.payload.progress,
              })
            );
            break;
          case "success":
            yield put(uploadSuccess({ id: action.payload.id }));
            yield put(
              trackEvent({
                name: "document_uploaded",
                params: {
                  file_name: action.payload.file.name,
                  file_size: action.payload.file.size,
                  file_type: action.payload.file.type,
                  document_id: event.payload.id,
                },
              })
            );
            break;
          case "error":
            yield put(
              uploadError({
                id: action.payload.id,
                error: `Storage error: ${event.payload}`,
              })
            );
            break;
        }
      }
    } finally {
      if (channel) {
        channel.close();
      }
    }
  } catch (error) {
    // Handle any errors that occur outside the channel (connection issues, etc.)
    const errorMessage =
      error instanceof Error
        ? error.message
        : "Upload failed. Please check your connection and try again.";
    yield put(
      uploadError({
        id: action.payload.id,
        error: errorMessage,
      })
    );

    if (channel) {
      channel.close();
    }
  }
}
